import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useWindowSize } from 'react-use';
import c from 'classnames';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useReactNodeView } from '@lyearn/lygo-core';

import HotspotContainer from './HotspotContainer';
import HotspotsRenderers from './HotspotsRenderers';
import InputPopover from './InputPopover';
import { containerDivHover } from './style';
import { HotspotsProps, HotspotType } from './types';

const Hotspot: React.FC<HotspotsProps> = (props) => {
  const {
    nodeElement,
    width,
    height,
    hotspots,
    readOnly,
    onChange,
    noCaption,
    allowTextUpdateOnly,
    renderPopoverContent,
    containInAnchorEl,
    popper,
  } = props;
  const [hotspotsState, setHotspotsState] = useState(hotspots || []);
  const [selectedHotspot, setSelectedHotspot] = useState<number | null>(null);
  const [open, { on, off }] = useToggle(false);
  const hotspotsRef = useRef<HTMLDivElement[]>([]);
  const { updateAttrs } = useReactNodeView();

  // When user clicks on the image, we are adding a hotspot and opening the input popover.
  // To give the anchor element to input popover, we need to make sure that hotspot
  // is mounted in the dom.
  useEffect(() => {
    if (open) {
      off();
      !noCaption && setSelectedHotspot(hotspotsState.length - 1);
    }
  }, [hotspotsState.length, noCaption, off, open]);

  const onChangeHotspotsState = useCallback(
    (updatedState: HotspotType[]) => {
      setHotspotsState(updatedState);
      onChange?.(updatedState);
    },
    [onChange],
  );

  return (
    <div
      className={c(
        readOnly ? 'overflow-hidden' : containerDivHover(selectedHotspot),
        'absolute h-full w-full',
      )}>
      {readOnly ? null : (
        <HotspotContainer
          hotspotsState={hotspotsState}
          nodeElement={nodeElement}
          setHotspotsState={onChangeHotspotsState}
          setNewHotspotAdded={on}
          setSelectedHotspot={setSelectedHotspot}
          updateAttrs={updateAttrs}
        />
      )}
      <HotspotsRenderers
        height={height}
        hotspotsRef={hotspotsRef}
        hotspotsState={hotspotsState}
        readOnly={readOnly}
        removeOnClick={noCaption && !readOnly ? true : undefined}
        selectedHotspot={selectedHotspot}
        setHotspotsState={onChangeHotspotsState}
        setSelectedHotspot={setSelectedHotspot}
        width={width}
      />
      {noCaption ? null : (
        <InputPopover
          allowTextUpdateOnly={allowTextUpdateOnly}
          anchorEl={
            Number.isInteger(selectedHotspot) ? hotspotsRef.current[selectedHotspot!] : null
          }
          containInAnchorEl={containInAnchorEl}
          height={height}
          hotspotsState={hotspotsState}
          popper={popper}
          readOnly={readOnly}
          renderPopoverContent={renderPopoverContent}
          selectedHotspot={selectedHotspot}
          setHotspotsState={onChangeHotspotsState}
          setSelectedHotspot={setSelectedHotspot}
          updateAttrs={updateAttrs}
          width={width}
        />
      )}
    </div>
  );
};

export default Hotspot;
